import React, { useEffect, useId, useMemo, useState } from "react";
import { SideDrawer, SideDrawerCloseButton } from "@dmc/ui";
import ApiData from "../../api/ApiData";
import { CcV4Toggle } from "../UI/CcV4Toggle";
import {
  isDefaultLicenseDescription,
  LICENSE_KIND_LABELS,
} from "../../utils/licenseCommentsUtils";

function formatLicenseCreated(value) {
  if (!value) {
    return "—";
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return "—";
  }
  return d.toISOString().slice(0, 10);
}

function buildVisibleRows(payload, categoryFlags, hideDefault) {
  const { showActiv, showFree, showInactive } = categoryFlags;
  if (!showActiv && !showFree && !showInactive) {
    return null;
  }

  const rows = [];
  if (showActiv) {
    (payload?.activ_license ?? []).forEach((item) => {
      rows.push({ item, licenseKind: "activ" });
    });
  }
  if (showFree) {
    (payload?.free_license ?? []).forEach((item) => {
      rows.push({ item, licenseKind: "free" });
    });
  }
  if (showInactive) {
    (payload?.no_activ ?? []).forEach((item) => {
      rows.push({ item, licenseKind: "inactive" });
    });
  }

  const filtered = hideDefault
    ? rows.filter(({ item }) => !isDefaultLicenseDescription(item.description))
    : rows;

  filtered.sort((a, b) => {
    const ta = new Date(a.item.created).getTime();
    const tb = new Date(b.item.created).getTime();
    return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
  });

  return filtered;
}

export function LicenseCommentsDrawer({ open, onClose, companyId }) {
  const titleId = useId();
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [showActiv, setShowActiv] = useState(true);
  const [showFree, setShowFree] = useState(true);
  const [showInactive, setShowInactive] = useState(true);
  const [hideDefault, setHideDefault] = useState(true);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setPayload(null);
    setShowActiv(true);
    setShowFree(true);
    setShowInactive(true);
    setHideDefault(true);

    ApiData.fetchLicensingSearchPage(companyId).then((data) => {
      if (!cancelled) {
        setPayload(data);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  const visibleRows = useMemo(
    () =>
      payload
        ? buildVisibleRows(
            payload,
            { showActiv, showFree, showInactive },
            hideDefault,
          )
        : [],
    [payload, showActiv, showFree, showInactive, hideDefault],
  );

  if (!open) {
    return null;
  }

  const counters = payload?.counters ?? {};
  const totalRecords = payload?.total_records ?? 0;

  const close = () => onClose?.();

  return (
    <SideDrawer
      open={open}
      onClose={close}
      title="информация по лицензиям"
      titleId={titleId}
      usePortal={false}
      toolbar={<SideDrawerCloseButton onClick={close} title="Закрыть" />}
    >
          <div className="license-comments-drawer-head">
            <div className="filters-bar license-comments-filters">
              <div className="filter-group">
                <CcV4Toggle
                  label="Активные"
                  active={showActiv}
                  onClick={() => setShowActiv((v) => !v)}
                />
                <CcV4Toggle
                  label="Свободные"
                  active={showFree}
                  onClick={() => setShowFree((v) => !v)}
                />
                <CcV4Toggle
                  label="Не активные"
                  active={showInactive}
                  onClick={() => setShowInactive((v) => !v)}
                />
                <CcV4Toggle
                  label="Не показывать DEFAULT"
                  active={hideDefault}
                  onClick={() => setHideDefault((v) => !v)}
                />
              </div>
            </div>

            <div className="metrics-chips license-comments-metrics">
              <div className="chip chip-total">
                <div className="chip-val">{totalRecords}</div>
                <div className="chip-lbl">Всего</div>
              </div>
              <div className="chip chip-active">
                <div className="chip-val">{counters.count_activ ?? 0}</div>
                <div className="chip-lbl">Активные</div>
              </div>
              <div className="chip chip-free">
                <div className="chip-val">{counters.count_free ?? 0}</div>
                <div className="chip-lbl">Свободные</div>
              </div>
              <div className="chip chip-inactive">
                <div className="chip-val">{counters.no_activ ?? 0}</div>
                <div className="chip-lbl">Не активные</div>
              </div>
            </div>
          </div>

          <div className="site-modal-body license-comments-drawer-body">
            {loading ? (
              <p className="license-comments-empty">Загрузка…</p>
            ) : visibleRows === null ? (
              <p className="license-comments-empty">
                Выберите категории для отображения
              </p>
            ) : visibleRows.length === 0 ? (
              <p className="license-comments-empty">Нет записей для отображения</p>
            ) : (
              <ul className="license-comments-list">
                {visibleRows.map(({ item, licenseKind }) => (
                  <li
                    key={`${licenseKind}-${item.id_license}`}
                    className="license-comments-item"
                  >
                    <div className="license-comments-field">
                      <span className="license-comments-label">Тип</span>
                      <span className="license-comments-value">
                        {LICENSE_KIND_LABELS[licenseKind]}
                      </span>
                    </div>
                    <div className="license-comments-field">
                      <span className="license-comments-label">Комментарий</span>
                      <span className="license-comments-value">
                        {String(item.description ?? "").trim()
                          ? item.description
                          : "—"}
                      </span>
                    </div>
                    <div className="license-comments-field">
                      <span className="license-comments-label">
                        Дата создания
                      </span>
                      <span className="license-comments-value">
                        {formatLicenseCreated(item.created)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
    </SideDrawer>
  );
}
